/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { FC, useCallback, useEffect, useMemo, useState, useRef } from 'react';
// import { Calendar, momentLocalizer } from 'react-big-calendar';
import { useIntl } from 'react-intl';
import axios from 'axios';
import ReactPaginate from 'react-paginate';
import { Link, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import moment from 'moment';
import ApexCharts, { ApexOptions } from 'apexcharts';
import { KTSVG } from 'src/_metronic/helpers';
import { MixedWidget1, MixedWidget2, MixedWidget3, MixedWidget4, MixedWidget6 } from 'src/_metronic/partials/widgets';
import { useControlGib, useEventsData, useEventsList, usePreSalesList } from 'src/app/modules/accounts/service';
import Restricted from 'src/app/permissions/restricted';
import usePermission from 'src/app/permissions/use-permission';
import { useAppSelector } from 'src/services/redux/app/store';
import { PageTitle } from '../../../_metronic/layout/core';
import { InviteCompany } from './_modals/invite-company';
import { TermsAndConditions } from './components/terms-and-conditions';
import { PaymentReceiveModal } from './modals/payment-receive';
import { useDashboardList, useDepQuery, useMajorCustomerQuery, useStockQuery, useCrtDiscountRateByProgramme } from './service/index';
import SalesTargetsWidget from '../sales-targets/SalesTargetsWidget';
import CustomerVisitsWidget from '../customer-visits/CustomerVisitsWidget';
import DashboardTargetsWidget from './DashboardTargetsWidget';
import DashboardWelcome from './DashboardWelcome';
import DashboardQuarterlyTargetWidget from './DashboardQuarterlyTargetWidget';
import DashboardInteractionsWidget from './DashboardInteractionsWidget';
import FirstInvoiceWidget from '../first-invoices/FirstInvoiceWidget';
import { DEPQueryWidget } from './DEPQueryWidget';
import QuickReservationModal from './modals/QuickReservationModal';
import ApolloContactModal from './modals/ApolloContactModal';
import ProductsOnTheWayWidget from './ProductsOnTheWayWidget';

type Props = {};

// Major Müşteri Program Badge Helper
const getProgrammeBadge = (programme: string | null) => {
  if (!programme) return '-';

  const programmeMap: { [key: string]: { label: string; badgeClass: string } } = {
    'Major Accounts Tier1 (14090)': { label: 'TIER1', badgeClass: 'badge-light-primary' },
    'Major Accounts Tier2 (14092)': { label: 'TIER2', badgeClass: 'badge-light-info' },
    'Major Accounts Tier3 (14094)': { label: 'TIER3', badgeClass: 'badge-light-success' },
    'Major Accounts Gov (14088)': { label: 'GOV', badgeClass: 'badge-light-danger' },
    'New to Mac (NCR S2W) (14288)': { label: 'NEW to MAC', badgeClass: 'badge-light-warning' },
  };

  const mapping = programmeMap[programme];
  if (mapping) {
    return <span className={`badge ${mapping.badgeClass} fw-bold`}>{mapping.label}</span>;
  }

  return <span className="badge badge-light-secondary">{programme}</span>;
};

// Fiyat Geçmişi Grafik Bileşeni
const PriceHistoryChart: FC<{ priceHistory: Array<{ date: string; price: number }> }> = ({ priceHistory }) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<ApexCharts | null>(null);

  useEffect(() => {
    if (!chartRef.current || !priceHistory || priceHistory.length === 0) return;

    // Verileri tarihe göre sırala (eskiden yeniye)
    const sortedData = [...priceHistory].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    const categories = sortedData.map((item) => moment(item.date).format('DD.MM'));
    const prices = sortedData.map((item) => parseFloat(String(item.price)) || 0);

    const options: ApexOptions = {
      chart: {
        type: 'area',
        height: 200,
        toolbar: { show: false },
        zoom: { enabled: false },
        fontFamily: 'inherit',
        sparkline: { enabled: false },
      },
      series: [
        {
          name: 'Fiyat',
          data: prices,
        },
      ],
      xaxis: {
        categories: categories,
        labels: {
          style: { fontSize: '10px', colors: '#A1A5B7' },
          rotate: -45,
          rotateAlways: priceHistory.length > 5,
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: {
        labels: {
          style: { fontSize: '10px', colors: '#A1A5B7' },
          formatter: (val: number) => `₺${val.toLocaleString('tr-TR')}`,
        },
      },
      stroke: {
        curve: 'smooth',
        width: 2,
      },
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.4,
          opacityTo: 0.1,
          stops: [0, 100],
        },
      },
      colors: ['#F1BC00'],
      grid: {
        borderColor: '#E4E6EF',
        strokeDashArray: 4,
        padding: { left: 10, right: 10 },
      },
      tooltip: {
        enabled: true,
        y: {
          formatter: (val: number) => `₺${val.toLocaleString('tr-TR')}`,
        },
        x: {
          formatter: (_val: number, opts: any) => {
            const idx = opts.dataPointIndex;
            return moment(sortedData[idx]?.date).format('DD.MM.YYYY HH:mm');
          },
        },
      },
      markers: {
        size: 4,
        colors: ['#F1BC00'],
        strokeColors: '#fff',
        strokeWidth: 2,
        hover: { size: 6 },
      },
      dataLabels: { enabled: false },
    };

    // Önceki chart'ı temizle
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    // Yeni chart oluştur
    chartInstance.current = new ApexCharts(chartRef.current, options);
    chartInstance.current.render();

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
    };
  }, [priceHistory]);

  return <div ref={chartRef} style={{ minHeight: '200px' }} />;
};

const DashboardPage: FC<Props> = ({}) => {
  // API hooks için error handler
  const onErrorFetch = (e: any) => e?.response?.data?.message && toast.error(e?.response?.data?.message);
  const { user } = useAppSelector((state: any) => state.auth);

  // Modal States
  const [showDepQueryModal, setShowDepQueryModal] = useState(false);
  const [showMajorCustomerModal, setShowMajorCustomerModal] = useState(false);
  const [showStockQueryModal, setShowStockQueryModal] = useState(false);
  const [showReservationModal, setShowReservationModal] = useState(false);
  const [showApolloModal, setShowApolloModal] = useState(false);

  // DEP Query States
  const [depSearchValue, setDepSearchValue] = useState('');
  const [depSearchType, setDepSearchType] = useState<'serial' | 'invoice'>('serial');
  const [depSearchResult, setDepSearchResult] = useState<any>(null);

  // DEP Query API Hook
  const { mutate: depQueryMutate, isLoading: depSearchLoading } = useDepQuery(
    (response: any) => {
      setDepSearchResult(response.data);
    },
    (error: any) => {
      toast.error(error?.response?.data?.message || 'DEP sorgulama hatası');
      setDepSearchResult(null);
    },
  );

  // Major Customer Query States
  const [majorCustomerName, setMajorCustomerName] = useState('');
  const [majorCustomerResult, setMajorCustomerResult] = useState<any>(null);
  const [majorDiscountRates, setMajorDiscountRates] = useState<any>(null);
  const [expandedCustomerId, setExpandedCustomerId] = useState<number | null>(null);
  const [expandedDiscountRates, setExpandedDiscountRates] = useState<any>(null);
  const [expandedLoading, setExpandedLoading] = useState(false);

  // CRT Discount Rate API Hook
  const { mutate: discountRateMutate, isLoading: discountRateLoading } = useCrtDiscountRateByProgramme(
    (response: any) => {
      setMajorDiscountRates(response.data);
    },
    () => {
      setMajorDiscountRates(null);
    },
  );

  // Major Customer Query API Hook
  const { mutate: majorCustomerQueryMutate, isLoading: majorCustomerLoading } = useMajorCustomerQuery(
    (response: any) => {
      setMajorCustomerResult(response.data);
      // Eğer müşteri bulunduysa ve tek bir sonuç varsa, indirim oranlarını getir
      if (response.data?.found && response.data?.data?.length === 1) {
        const programme = response.data.data[0]?.programme;
        if (programme) {
          // Programme'dan parantez içindeki kodu çıkar - örn: "Major Accounts Tier1 (14090)" -> "Major Accounts Tier1"
          const programmeName = programme.replace(/\s*\([^)]*\)\s*$/, '');
          discountRateMutate({ programme: programmeName });
        }
      } else {
        setMajorDiscountRates(null);
      }
    },
    (error: any) => {
      toast.error(error?.response?.data?.message || 'Major müşteri sorgulama hatası');
      setMajorCustomerResult(null);
      setMajorDiscountRates(null);
    },
  );

  // Stock Query States
  const [stockCode, setStockCode] = useState('');
  const [stockResult, setStockResult] = useState<any>(null);

  // Stock Query API Hook
  const { mutate: stockQueryMutate, isLoading: stockLoading } = useStockQuery(
    (response: any) => {
      setStockResult(response.data);
    },
    (error: any) => {
      toast.error(error?.response?.data?.message || 'Stok sorgulama hatası');
      setStockResult(null);
    },
  );

  // DEP Query Handler
  const handleDepQuery = () => {
    if (!depSearchValue.trim()) {
      toast.warning(depSearchType === 'serial' ? 'Lütfen seri numarası giriniz' : 'Lütfen fatura numarası giriniz');
      return;
    }
    depQueryMutate({ searchValue: depSearchValue.trim(), searchType: depSearchType });
  };

  // Major Customer Query Handler
  const handleMajorCustomerQuery = () => {
    if (!majorCustomerName.trim()) {
      toast.warning('Lütfen müşteri adı veya kodu giriniz');
      return;
    }
    majorCustomerQueryMutate({ searchValue: majorCustomerName.trim() });
  };

  // Stock Query Handler
  const handleStockQuery = () => {
    if (!stockCode.trim()) {
      toast.warning('Lütfen stok kodu giriniz');
      return;
    }
    stockQueryMutate({ searchValue: stockCode.trim() });
  };

  // Modal kapatıldığında state temizle
  const handleCloseDepModal = () => {
    setShowDepQueryModal(false);
    setDepSearchValue('');
    setDepSearchType('serial');
    setDepSearchResult(null);
  };

  const handleCloseMajorCustomerModal = () => {
    setShowMajorCustomerModal(false);
    setMajorCustomerName('');
    setMajorCustomerResult(null);
    setMajorDiscountRates(null);
    setExpandedCustomerId(null);
    setExpandedDiscountRates(null);
  };

  // Müşteri satırındaki indirim oranlarını genişlet/daralt
  const handleToggleDiscountRates = async (customerId: number, programme: string) => {
    if (expandedCustomerId === customerId) {
      // Zaten açıksa kapat
      setExpandedCustomerId(null);
      setExpandedDiscountRates(null);
    } else {
      // Yeni satır aç ve indirim oranlarını getir
      setExpandedCustomerId(customerId);
      setExpandedDiscountRates(null);
      setExpandedLoading(true);
      try {
        const programmeName = programme.replace(/\s*\([^)]*\)\s*$/, '');
        const { REACT_APP_API_URL } = process.env;
        const response = await axios.get(`${REACT_APP_API_URL}/crt-discount-rates/by-programme`, {
          params: { programme: programmeName },
        });
        setExpandedDiscountRates(response.data);
      } catch (error) {
        setExpandedDiscountRates(null);
      } finally {
        setExpandedLoading(false);
      }
    }
  };

  const handleCloseStockModal = () => {
    setShowStockQueryModal(false);
    setStockCode('');
    setStockResult(null);
  };

  // Hızlı İşlemler için izin kontrolleri
  const [depPermLoading, hasDepPermission] = usePermission('ROLE_ENROLLMENTS_QUERY');
  const [majorPermLoading, hasMajorPermission] = usePermission('ROLE_CRM_REBATE_MAJORS');
  const [stockPermLoading, hasStockPermission] = usePermission('ROLE_REPORT_STOCK');
  const [reservPermLoading, hasReservPermission] = usePermission('ROLE_STOCK_RESERVATION');
  const [quotesPermLoading, hasQuotesPermission] = usePermission('ROLE_CRM_QUOTES');
  const [apolloPermLoading, hasApolloPermission] = usePermission('ROLE_DASHBOARD_APOLLO');

  // Hiçbir izin yoksa hızlı işlemleri gizle
  const showQuickQueries =
    !depPermLoading &&
    !majorPermLoading &&
    !stockPermLoading &&
    !reservPermLoading &&
    !quotesPermLoading &&
    !apolloPermLoading &&
    (hasDepPermission || hasMajorPermission || hasStockPermission || hasReservPermission || hasQuotesPermission || hasApolloPermission);

  return (
    <>
      {/* Hızlı Sorgu Butonları */}
      {showQuickQueries && (
        <div className="row mb-5">
          <div className="col-12">
            <div className="card">
              <div className="card-body py-4">
                <div className="d-flex align-items-center">
                  <span className="fw-bold text-gray-700 me-3 d-none d-md-block">
                    <i className="bi bi-lightning-charge-fill text-warning me-2"></i>
                    Hızlı İşlemler:
                  </span>
                  <div className="d-md-none overflow-hidden flex-grow-1" style={{ paddingTop: 10 }}>
                    <div
                      className="d-flex gap-3"
                      style={{
                        animation: 'quickQueryScroll 8s ease-in-out infinite alternate',
                        width: 'max-content',
                      }}
                    >
                      <Restricted to="ROLE_CRM_QUOTES">
                        <Link to="/orders/create" className="btn btn-sm btn-light-danger d-flex align-items-center gap-2 flex-shrink-0 position-relative">
                          <KTSVG path="/media/icons/duotone/Shopping/Cart3.svg" className="svg-icon-3" />
                          Sipariş Oluştur
                          {/* <span className="badge badge-danger fw-bold position-absolute" style={{ top: -8, right: -8, fontSize: '0.6rem', padding: '3px 6px', zIndex: 3 }}>
                            Yeni
                          </span> */}
                        </Link>
                      </Restricted>
                      <Restricted to="ROLE_STOCK_RESERVATION">
                        <button className="btn btn-sm btn-light-warning d-flex align-items-center gap-2 flex-shrink-0" onClick={() => setShowReservationModal(true)}>
                          <KTSVG path="/media/icons/duotone/General/Lock.svg" className="svg-icon-3" />
                          Ürün Rezerv Al
                        </button>
                      </Restricted>
                      <Restricted to="ROLE_ENROLLMENTS_QUERY">
                        <button className="btn btn-sm btn-light-primary d-flex align-items-center gap-2 flex-shrink-0" onClick={() => setShowDepQueryModal(true)}>
                          <KTSVG path="/media/icons/duotone/General/Search.svg" className="svg-icon-3" />
                          DEP Kaydı Sorgula
                        </button>
                      </Restricted>
                      <Restricted to="ROLE_CRM_REBATE_MAJORS">
                        <button className="btn btn-sm btn-light-success d-flex align-items-center gap-2 flex-shrink-0" onClick={() => setShowMajorCustomerModal(true)}>
                          <KTSVG path="/media/icons/duotone/General/Search.svg" className="svg-icon-3" />
                          Major Müşteri Sorgula
                        </button>
                      </Restricted>
                      <Restricted to="ROLE_REPORT_STOCK">
                        <button className="btn btn-sm btn-light-info d-flex align-items-center gap-2 flex-shrink-0" onClick={() => setShowStockQueryModal(true)}>
                          <KTSVG path="/media/icons/duotone/General/Search.svg" className="svg-icon-3" />
                          Stok Sorgula
                        </button>
                      </Restricted>
                      <Restricted to="ROLE_DASHBOARD_APOLLO">
                        <button className="btn btn-sm btn-light-dark d-flex align-items-center gap-2 flex-shrink-0" onClick={() => setShowApolloModal(true)}>
                          <KTSVG path="/media/icons/duotone/Communication/Address-card.svg" className="svg-icon-3" />
                          İletişim Bilgisi Sorgula
                        </button>
                      </Restricted>
                    </div>
                  </div>
                  <div className="d-none d-md-flex gap-3">
                    <Restricted to="ROLE_CRM_QUOTES">
                      <Link to="/orders/create" className="btn btn-sm btn-light-danger d-flex align-items-center gap-2 position-relative">
                        <KTSVG path="/media/icons/duotone/Shopping/Cart3.svg" className="svg-icon-3" />
                        Sipariş Oluştur
                        <span className="badge badge-danger fw-bold position-absolute" style={{ top: -8, right: -8, fontSize: '0.6rem', padding: '3px 6px', zIndex: 3 }}>
                          Yeni
                        </span>
                      </Link>
                    </Restricted>
                    <Restricted to="ROLE_STOCK_RESERVATION">
                      <button className="btn btn-sm btn-light-warning d-flex align-items-center gap-2" onClick={() => setShowReservationModal(true)}>
                        <KTSVG path="/media/icons/duotone/General/Lock.svg" className="svg-icon-3" />
                        Ürün Rezerv Al
                      </button>
                    </Restricted>
                    <Restricted to="ROLE_ENROLLMENTS_QUERY">
                      <button className="btn btn-sm btn-light-primary d-flex align-items-center gap-2" onClick={() => setShowDepQueryModal(true)}>
                        <KTSVG path="/media/icons/duotone/General/Search.svg" className="svg-icon-3" />
                        DEP Kaydı Sorgula
                      </button>
                    </Restricted>
                    <Restricted to="ROLE_CRM_REBATE_MAJORS">
                      <button className="btn btn-sm btn-light-success d-flex align-items-center gap-2" onClick={() => setShowMajorCustomerModal(true)}>
                        <KTSVG path="/media/icons/duotone/General/Search.svg" className="svg-icon-3" />
                        Major Müşteri Sorgula
                      </button>
                    </Restricted>
                    <Restricted to="ROLE_REPORT_STOCK">
                      <button className="btn btn-sm btn-light-info d-flex align-items-center gap-2" onClick={() => setShowStockQueryModal(true)}>
                        <KTSVG path="/media/icons/duotone/General/Search.svg" className="svg-icon-3" />
                        Stok Sorgula
                      </button>
                    </Restricted>
                    <Restricted to="ROLE_DASHBOARD_APOLLO">
                      <button className="btn btn-sm btn-light-dark d-flex align-items-center gap-2" onClick={() => setShowApolloModal(true)}>
                        <KTSVG path="/media/icons/duotone/Communication/Address-card.svg" className="svg-icon-3" />
                        İletişim Bilgisi Sorgula
                      </button>
                    </Restricted>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobil için CSS animasyonu */}
      <style>{`
        @keyframes quickQueryScroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(calc(-100% + 100vw - 60px)); }
        }
      `}</style>

      <div className="row">
        {/* begin::Col */}
        {/* <div className="col-xl-6 mb-5">
          <MixedWidget1 className="card-xl-stretch mb-xl-8" color="success bg-gradient" dashboardList={dashboardList?.monthlyFinance[0]} />
        </div> */}
        {/* end::Col */}

        <div className="row">
          <DashboardTargetsWidget />
        </div>
        <div className="row">
          <DashboardQuarterlyTargetWidget />
        </div>
        {/* <div className="row g-5 g-xl-8">
          <SalesTargetsWidget />
        </div> */}
        <div className="row">
          <DashboardInteractionsWidget />
        </div>
        <div className="row">
          <CustomerVisitsWidget />
          <FirstInvoiceWidget />
        </div>
        <div className="row">
          <ProductsOnTheWayWidget />
        </div>
        {/* <div className="row">
          <DEPQueryWidget className="card-xl-stretch mb-xl-8" chartColor="primary" chartHeight="150px" />
        </div> */}

        {/* begin::Col */}
        {/* <div className="col-xl-6 mb-5">
          <MixedWidget6 className="card-xl-stretch mb-xl-8" chartColor="primary" chartHeight="230px" dashboardList={dashboardList?.currentActivity} />
        </div> */}

        {/* end::Col */}
      </div>

      {/* DEP Kaydı Sorgula Modal */}
      {showDepQueryModal && (
        <div
          className="modal fade show d-block"
          tabIndex={-1}
          style={{ backgroundColor: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' }}
          onClick={handleCloseDepModal}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title fw-bold">
                  <i className="bi bi-phone text-primary me-2"></i>
                  DEP Kaydı Sorgula
                </h5>
                <button type="button" className="btn btn-sm btn-icon btn-light-primary" onClick={handleCloseDepModal}>
                  <KTSVG path="/media/icons/duotone/Navigation/Close.svg" className="svg-icon-1" />
                </button>
              </div>
              <div className="modal-body">
                {/* Arama Tipi Seçimi */}
                <div className="mb-4">
                  <label className="form-label fw-semibold">Arama Tipi</label>
                  <div className="d-flex gap-4">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="depSearchType"
                        id="searchTypeSerial"
                        checked={depSearchType === 'serial'}
                        onChange={() => {
                          setDepSearchType('serial');
                          setDepSearchResult(null);
                        }}
                      />
                      <label className="form-check-label" htmlFor="searchTypeSerial">
                        Seri Numarası ile Ara
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="depSearchType"
                        id="searchTypeInvoice"
                        checked={depSearchType === 'invoice'}
                        onChange={() => {
                          setDepSearchType('invoice');
                          setDepSearchResult(null);
                        }}
                      />
                      <label className="form-check-label" htmlFor="searchTypeInvoice">
                        Fatura Numarası ile Ara
                      </label>
                    </div>
                  </div>
                </div>

                {/* Arama Input */}
                <div className="mb-4">
                  <label className="form-label fw-semibold">{depSearchType === 'serial' ? 'Seri Numarası' : 'Fatura Numarası'}</label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      placeholder={depSearchType === 'serial' ? 'Seri numarası giriniz...' : 'Fatura numarası giriniz...'}
                      value={depSearchValue}
                      onChange={(e) => setDepSearchValue(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleDepQuery()}
                    />
                    <button className="btn btn-primary" onClick={handleDepQuery} disabled={depSearchLoading}>
                      {depSearchLoading ? (
                        <span className="spinner-border spinner-border-sm"></span>
                      ) : (
                        <>
                          <i className="bi bi-search me-1"></i>
                          Sorgula
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Sonuç Alanı */}
                {depSearchResult && (
                  <div className={`alert ${depSearchResult.found ? 'alert-success' : 'alert-warning'} mb-0`}>
                    <div className="d-flex align-items-center mb-3">
                      <i className={`bi ${depSearchResult.found ? 'bi-check-circle-fill' : 'bi-exclamation-triangle-fill'} fs-3 me-2`}></i>
                      <span className="fw-bold">{depSearchResult.message}</span>
                    </div>

                    {/* Fatura Numarası ile Arama Sonuçları */}
                    {depSearchResult.found && depSearchResult.searchType === 'invoice' && depSearchResult.data && depSearchResult.data.length > 0 && (
                      <div className="d-flex flex-column gap-3">
                        {depSearchResult.data.map((item: any, index: number) => (
                          <div key={index} className="bg-white rounded p-4 border">
                            {/* Enrollment Bilgileri */}
                            <h6 className="fw-bold text-success mb-3">
                              <i className="bi bi-receipt me-2"></i>
                              Fatura Bilgileri
                              <span className={`badge ${item.deviceCount > 0 ? 'badge-light-primary' : 'badge-light-warning'} ms-2`}>
                                {item.deviceCount > 0 ? `${item.deviceCount} cihaz` : 'Cihaz kaydı yok'}
                              </span>
                            </h6>
                            <div className="row g-3 mb-3">
                              <div className="col-md-4">
                                <div className="d-flex flex-column">
                                  <span className="text-muted fs-9">Fatura No</span>
                                  <span className="fw-bold">{item.enrollment?.ordernumber || '-'}</span>
                                </div>
                              </div>
                              <div className="col-md-4">
                                <div className="d-flex flex-column">
                                  <span className="text-muted fs-9">Sipariş Tarihi</span>
                                  <span className="fw-bold">{item.enrollment?.orderdate ? moment(item.enrollment.orderdate).format('DD.MM.YYYY') : '-'}</span>
                                </div>
                              </div>
                              <div className="col-md-4">
                                <div className="d-flex flex-column">
                                  <span className="text-muted fs-9">Sevk Tarihi</span>
                                  <span className="fw-bold">{item.enrollment?.shipdate ? moment(item.enrollment.shipdate).format('DD.MM.YYYY') : '-'}</span>
                                </div>
                              </div>
                              <div className="col-md-8">
                                <div className="d-flex flex-column">
                                  <span className="text-muted fs-9">Müşteri Adı</span>
                                  <span className="fw-bold">{item.enrollment?.customer_name || item.enrollment?.customerid || '-'}</span>
                                </div>
                              </div>
                              <div className="col-md-4">
                                <div className="d-flex flex-column">
                                  <span className="text-muted fs-9">Enrollment Durumu</span>
                                  <span className={`badge ${item.enrollment?.status === 'COMPLETE' ? 'badge-light-success' : 'badge-light-info'} w-auto`}>
                                    {item.enrollment?.status || '-'}
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* Cihaz Listesi */}
                            {item.devices && item.devices.length > 0 ? (
                              <>
                                <div className="separator my-3"></div>
                                <h6 className="fw-bold text-primary mb-3">
                                  <i className="bi bi-phone me-2"></i>
                                  Cihazlar ({item.devices.length})
                                </h6>
                                <div className="table-responsive">
                                  <table className="table table-row-bordered table-sm mb-0">
                                    <thead>
                                      <tr className="fw-bold text-muted fs-9">
                                        <th>Seri No</th>
                                        <th>Ürün Açıklaması</th>
                                        <th>DEP Durumu</th>
                                        <th>Kayıt Tarihi</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {item.devices.map((device: any, dIdx: number) => (
                                        <tr key={dIdx}>
                                          <td className="fw-bold">{device.serialnumber || '-'}</td>
                                          <td>{device.assettag || '-'}</td>
                                          <td>
                                            <span className={`badge ${device.devicepoststatus === 'COMPLETE' ? 'badge-light-success' : 'badge-light-warning'}`}>
                                              {device.devicepoststatus || '-'}
                                            </span>
                                          </td>
                                          <td>{device.created_at ? moment(device.created_at).format('DD.MM.YYYY HH:mm') : '-'}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </>
                            ) : (
                              <div className="text-center text-muted py-3 border-top mt-3">
                                <i className="bi bi-phone-vibrate fs-2 mb-2 d-block"></i>
                                <span>Bu fatura için cihaz kaydı bulunmamaktadır.</span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Seri Numarası ile Arama Sonuçları */}
                    {depSearchResult.found && depSearchResult.searchType === 'serial' && depSearchResult.data && depSearchResult.data.length > 0 && (
                      <div className="d-flex flex-column gap-3">
                        {depSearchResult.data.map((item: any, index: number) => (
                          <div key={index} className="bg-white rounded p-4 border">
                            {/* Device Bilgileri */}
                            <h6 className="fw-bold text-primary mb-3">
                              <i className="bi bi-phone me-2"></i>
                              Cihaz Bilgileri
                            </h6>
                            <div className="row g-3 mb-3">
                              <div className="col-md-4">
                                <div className="d-flex flex-column">
                                  <span className="text-muted fs-9">Seri No</span>
                                  <span className="fw-bold">{item.device?.serialnumber || '-'}</span>
                                </div>
                              </div>
                              <div className="col-md-4">
                                <div className="d-flex flex-column">
                                  <span className="text-muted fs-9">Fatura No</span>
                                  <span className="fw-bold">{item.device?.ordernumber || '-'}</span>
                                </div>
                              </div>
                              <div className="col-md-4">
                                <div className="d-flex flex-column">
                                  <span className="text-muted fs-9">Kayıt Tarihi</span>
                                  <span className="fw-bold">{item.device?.created_at ? moment(item.device.created_at).format('DD.MM.YYYY HH:mm') : '-'}</span>
                                </div>
                              </div>
                              <div className="col-md-8">
                                <div className="d-flex flex-column">
                                  <span className="text-muted fs-9">Ürün Açıklaması</span>
                                  <span className="fw-bold">{item.device?.assettag || '-'}</span>
                                </div>
                              </div>
                              <div className="col-md-4">
                                <div className="d-flex flex-column">
                                  <span className="text-muted fs-9">Ürün DEP Durumu</span>
                                  <span className={`badge ${item.device?.devicepoststatus === 'COMPLETE' ? 'badge-light-success' : 'badge-light-warning'} w-auto`}>
                                    {item.device?.devicepoststatus || '-'}
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* Enrollment Bilgileri */}
                            {item.enrollment && (
                              <>
                                <div className="separator my-3"></div>
                                <h6 className="fw-bold text-success mb-3">
                                  <i className="bi bi-clipboard-check me-2"></i>
                                  Enrollment Bilgileri
                                </h6>
                                <div className="row g-3">
                                  <div className="col-md-6">
                                    <div className="d-flex flex-column">
                                      <span className="text-muted fs-9">Sipariş Tarihi</span>
                                      <span className="fw-bold">{item.enrollment.orderdate ? moment(item.enrollment.orderdate).format('DD.MM.YYYY') : '-'}</span>
                                    </div>
                                  </div>
                                  <div className="col-md-6">
                                    <div className="d-flex flex-column">
                                      <span className="text-muted fs-9">Sevk Tarihi</span>
                                      <span className="fw-bold">{item.enrollment.shipdate ? moment(item.enrollment.shipdate).format('DD.MM.YYYY') : '-'}</span>
                                    </div>
                                  </div>
                                  <div className="col-md-8">
                                    <div className="d-flex flex-column">
                                      <span className="text-muted fs-9">Müşteri Adı</span>
                                      <span className="fw-bold">{item.enrollment.customer_name || item.enrollment.customerid || '-'}</span>
                                    </div>
                                  </div>

                                  <div className="col-md-4">
                                    <div className="d-flex flex-column">
                                      <span className="text-muted fs-9">Enrollment Durumu</span>
                                      <span className={`badge ${item.enrollment.status === 'COMPLETE' ? 'badge-light-success' : 'badge-light-info'} w-auto`}>
                                        {item.enrollment.status || '-'}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-light" onClick={handleCloseDepModal}>
                  Kapat
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Major Müşteri Sorgula Modal */}
      {showMajorCustomerModal && (
        <div
          className="modal fade show d-block"
          tabIndex={-1}
          style={{ backgroundColor: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' }}
          onClick={handleCloseMajorCustomerModal}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title fw-bold">
                  <i className="bi bi-building text-success me-2"></i>
                  Major Müşteri Sorgula
                </h5>
                <button type="button" className="btn btn-sm btn-icon btn-light-success" onClick={handleCloseMajorCustomerModal}>
                  <KTSVG path="/media/icons/duotone/Navigation/Close.svg" className="svg-icon-1" />
                </button>
              </div>
              <div className="modal-body">
                {/* Arama Input */}
                <div className="mb-4">
                  <label className="form-label fw-semibold">Müşteri Adı veya Kodu</label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Müşteri adı veya kodu giriniz..."
                      value={majorCustomerName}
                      onChange={(e) => setMajorCustomerName(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleMajorCustomerQuery()}
                    />
                    <button className="btn btn-success" onClick={handleMajorCustomerQuery} disabled={majorCustomerLoading}>
                      {majorCustomerLoading ? (
                        <span className="spinner-border spinner-border-sm"></span>
                      ) : (
                        <>
                          <i className="bi bi-search me-1"></i>
                          Sorgula
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Sonuç Alanı */}
                {majorCustomerResult && (
                  <div className={`alert ${majorCustomerResult.found ? 'alert-success' : 'alert-warning'} mb-0`}>
                    <div className="d-flex align-items-center mb-3">
                      <i className={`bi ${majorCustomerResult.found ? 'bi-check-circle-fill' : 'bi-exclamation-triangle-fill'} fs-3 me-2`}></i>
                      <span className="fw-bold">{majorCustomerResult.message}</span>
                    </div>

                    {/* Müşteri Listesi */}
                    {majorCustomerResult.found && majorCustomerResult.data && majorCustomerResult.data.length > 0 && (
                      <>
                        <div className="table-responsive">
                          <table className="table table-row-bordered table-sm mb-0 bg-white">
                            <thead>
                              <tr className="fw-bold text-muted fs-9">
                                <th>Müşteri Adı</th>
                                <th>Program</th>
                                {majorCustomerResult.data.length > 1 && (
                                  <th className="text-center" style={{ width: '80px' }}>
                                    İndirim
                                  </th>
                                )}
                              </tr>
                            </thead>
                            <tbody>
                              {majorCustomerResult.data.map((customer: any) => (
                                <React.Fragment key={customer.id}>
                                  <tr>
                                    <td>{customer.customer_name || '-'}</td>
                                    <td>{getProgrammeBadge(customer.programme)}</td>
                                    {majorCustomerResult.data.length > 1 && (
                                      <td className="text-center">
                                        <button
                                          className={`btn btn-sm ${expandedCustomerId === customer.id ? 'btn-success' : 'btn-light-success'} btn-icon`}
                                          onClick={() => handleToggleDiscountRates(customer.id, customer.programme)}
                                          title="İndirim Oranlarını Göster"
                                        >
                                          <KTSVG path="/media/icons/duotone/Shopping/Sale1.svg" className="svg-icon-1x svg-icon-info " />

                                          {/* <i className={`bi ${expandedCustomerId === customer.id ? 'bi-chevron-up' : 'bi-percent'}`}></i> */}
                                        </button>
                                      </td>
                                    )}
                                  </tr>
                                  {majorCustomerResult.data.length > 1 && expandedCustomerId === customer.id && (
                                    <tr>
                                      <td colSpan={3} className="p-0">
                                        <div className="bg-light-success p-3">
                                          {expandedLoading ? (
                                            <div className="text-center py-2">
                                              <span className="spinner-border spinner-border-sm text-success me-2"></span>
                                              <span className="text-muted">İndirim oranları yükleniyor...</span>
                                            </div>
                                          ) : expandedDiscountRates && expandedDiscountRates.urun_siniflari_oranlari ? (
                                            <div className="d-flex flex-wrap gap-2">
                                              {Object.entries(expandedDiscountRates.urun_siniflari_oranlari)
                                                .sort((a: any, b: any) => b[1] - a[1])
                                                .map(([productClass, discountRate]: [string, any], idx: number) => (
                                                  <div key={idx} className="d-flex align-items-center bg-white rounded px-2 py-1 border">
                                                    <span className="text-gray-700 fs-8 me-2">{productClass}</span>
                                                    <span
                                                      className={`badge ${
                                                        discountRate >= 10
                                                          ? 'badge-danger'
                                                          : discountRate >= 5
                                                          ? 'badge-warning'
                                                          : discountRate >= 3
                                                          ? 'badge-info'
                                                          : 'badge-light-primary'
                                                      } fs-8`}
                                                    >
                                                      %{discountRate}
                                                    </span>
                                                  </div>
                                                ))}
                                            </div>
                                          ) : (
                                            <div className="text-center py-2 text-muted">
                                              <i className="bi bi-exclamation-circle me-2"></i>
                                              Bu program için indirim oranı bulunamadı
                                            </div>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        {/* Tek sonuç için otomatik indirim oranları gösterimi */}
                        {majorCustomerResult.data.length === 1 && discountRateLoading && (
                          <div className="text-center py-3 mt-3 border-top">
                            <span className="spinner-border spinner-border-sm text-success me-2"></span>
                            <span className="text-muted">İndirim oranları yükleniyor...</span>
                          </div>
                        )}
                        {majorCustomerResult.data.length === 1 && majorDiscountRates && majorDiscountRates.urun_siniflari_oranlari && (
                          <div className="mt-4 pt-3 border-top">
                            <h6 className="fw-bold text-success mb-3">
                              <i className="bi bi-percent me-2"></i>
                              Program İndirim Oranları
                            </h6>
                            <div className="d-flex flex-wrap gap-2">
                              {Object.entries(majorDiscountRates.urun_siniflari_oranlari)
                                .sort((a: any, b: any) => b[1] - a[1])
                                .map(([productClass, discountRate]: [string, any], idx: number) => (
                                  <div key={idx} className="d-flex align-items-center bg-white rounded px-3 py-2 border">
                                    <span className="text-gray-700 fs-7 me-2">{productClass}</span>
                                    <span
                                      className={`badge ${
                                        discountRate >= 10 ? 'badge-danger' : discountRate >= 5 ? 'badge-warning' : discountRate >= 3 ? 'badge-info' : 'badge-light-primary'
                                      } fs-7`}
                                    >
                                      %{discountRate}
                                    </span>
                                  </div>
                                ))}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-light" onClick={handleCloseMajorCustomerModal}>
                  Kapat
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stok Sorgula Modal */}
      {showStockQueryModal && (
        <div
          className="modal fade show d-block"
          tabIndex={-1}
          style={{ backgroundColor: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' }}
          onClick={handleCloseStockModal}
        >
          <div className="modal-dialog modal-dialog-centered modal-xl" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content border-0 shadow-lg">
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title fw-bold d-flex align-items-center gap-2">
                  <KTSVG path="/media/icons/duotone/General/Search.svg" className="svg-icon-3" />
                  Stok Sorgula
                </h5>
                <button type="button" className="btn btn-sm btn-icon btn-light-info" onClick={handleCloseStockModal}>
                  <KTSVG path="/media/icons/duotone/Navigation/Close.svg" className="svg-icon-1" />
                </button>
              </div>
              <div className="modal-body pt-2">
                {/* Arama Input */}
                <div className="input-group mb-4">
                  <input
                    type="text"
                    className="form-control form-control-solid"
                    placeholder="Stok kodu giriniz..."
                    value={stockCode}
                    onChange={(e) => setStockCode(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleStockQuery()}
                  />
                  <button
                    className="btn btn-info px-6 d-flex align-items-center justify-content-center"
                    onClick={handleStockQuery}
                    disabled={stockLoading}
                    style={{ minWidth: '50px' }}
                  >
                    {stockLoading ? <span className="spinner-border spinner-border-sm"></span> : <KTSVG path="/media/icons/duotone/General/Search.svg" className="svg-icon-2" />}
                  </button>
                </div>

                {/* Sonuç Alanı */}
                {stockResult && !stockResult.found && (
                  <div className="text-center py-6 text-muted">
                    <i className="bi bi-exclamation-circle fs-1 mb-2 d-block"></i>
                    <span>{stockResult.message}</span>
                  </div>
                )}

                {stockResult?.found && stockResult.data && (
                  <div className="d-flex flex-column gap-4">
                    {/* Üst Bilgi Kartları */}
                    <div className="row g-3">
                      {/* DIA Stok */}
                      <div className="col-6 col-md-3">
                        <div className="bg-light-primary rounded p-3 text-center h-100">
                          <div className="text-primary fs-9 mb-1">DIA Stok</div>
                          <div className="fw-bold fs-2 text-primary">{stockResult.data.diaStock?.[0]?.realStock ?? <span className="fs-7 text-muted">-</span>}</div>
                        </div>
                      </div>
                      {/* Piyasa Fiyatı */}
                      <div className="col-6 col-md-3">
                        <div className="bg-light-success rounded p-3 text-center h-100">
                          <div className="text-success fs-9 mb-1">Piyasa Fiyatı</div>
                          <div className="fw-bold fs-4 text-success">
                            {stockResult.data.product?.price > 0 ? `₺${stockResult.data.product.price.toLocaleString('tr-TR')}` : <span className="fs-7 text-muted">-</span>}
                          </div>
                        </div>
                      </div>
                      {/* Maliyet */}
                      <div className="col-6 col-md-3">
                        <div className="bg-light-danger rounded p-3 text-center h-100">
                          <div className="text-danger fs-9 mb-1">Maliyet</div>
                          <div className="fw-bold fs-4 text-danger">
                            {stockResult.data.product?.cost > 0 ? `₺${stockResult.data.product.cost.toLocaleString('tr-TR')}` : <span className="fs-7 text-muted">-</span>}
                          </div>
                        </div>
                      </div>
                      {/* Yoldaki */}
                      <div className="col-6 col-md-3">
                        <div className="bg-light-info rounded p-3 text-center h-100">
                          <div className="text-info fs-9 mb-1">Yoldaki</div>
                          <div className="fw-bold fs-2 text-info">{stockResult.data.onTheWay?.totalStock || <span className="fs-7 text-muted">-</span>}</div>
                        </div>
                      </div>
                    </div>

                    {/* Ek Bilgiler */}
                    {stockResult.data.product && (
                      <div className="d-flex flex-wrap gap-3 text-muted fs-8 justify-content-between">
                        {stockResult.data.product.group && (
                          <span>
                            <i className="bi bi-tag me-1"></i>
                            {stockResult.data.product.group} - {stockResult.data.diaStock?.[0]?.description}
                          </span>
                        )}
                        {stockResult.data.product.updated_at && (
                          <span>
                            <i className="bi bi-clock me-1"></i>
                            {moment(stockResult.data.product.updated_at).format('DD.MM.YYYY HH:mm')}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Fiyat Geçmişi Grafiği */}
                    {stockResult.data.priceHistory?.length > 0 && (
                      <div className="border rounded p-3">
                        <div className="d-flex align-items-center justify-content-between mb-2">
                          <span className="fw-semibold fs-7 text-gray-700">
                            <i className="bi bi-graph-up text-warning me-2"></i>Piyasa Fiyat Geçmişi
                          </span>
                        </div>
                        <PriceHistoryChart priceHistory={stockResult.data.priceHistory} />
                      </div>
                    )}

                    {/* Yoldaki Ürünler Tablosu */}
                    {stockResult.data.onTheWay?.items?.length > 0 && (
                      <div className="border rounded p-3">
                        <div className="fw-semibold fs-7 text-gray-700 mb-3">
                          <i className="bi bi-truck text-info me-2"></i>Yoldaki Ürünler
                        </div>
                        <div className="table-responsive" style={{ maxHeight: '180px' }}>
                          <table className="table table-sm table-row-dashed mb-0 fs-8">
                            <thead>
                              <tr className="text-muted">
                                <th>Tarih</th>
                                <th>PO</th>
                                <th className="text-end">Adet</th>
                                <th>Sipariş</th>
                              </tr>
                            </thead>
                            <tbody>
                              {stockResult.data.onTheWay.items.map((item: any) => (
                                <tr key={item.id}>
                                  <td>{item.orderDate ? moment(item.orderDate).format('DD.MM') : '-'}</td>
                                  <td className="fw-semibold">{item.po || '-'}</td>
                                  <td className="text-end fw-bold text-success">{item.stock}</td>
                                  <td className="text-muted">{item.orderBy || '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Teklif Geçmişi */}
                    {stockResult.data.quoteHistory?.length > 0 && (
                      <div className="border rounded p-3">
                        <div className="d-flex align-items-center justify-content-between mb-3">
                          <span className="fw-semibold fs-7 text-gray-700">
                            <i className="bi bi-file-earmark-text text-primary me-2"></i>Teklif Geçmişi
                          </span>
                          <span className="badge badge-light-primary fs-9">{stockResult.data.quoteHistory.length} teklif</span>
                        </div>
                        <div className="table-responsive" style={{ maxHeight: '220px' }}>
                          <table className="table table-sm table-row-dashed mb-0 fs-8">
                            <thead>
                              <tr className="text-muted">
                                <th>Tarih</th>
                                <th>Teklif No</th>
                                <th>Müşteri</th>
                                <th className="text-end">Adet</th>
                                <th className="text-end">Birim Fiyat</th>
                                <th className="text-center">Durum</th>
                              </tr>
                            </thead>
                            <tbody>
                              {stockResult.data.quoteHistory.map((qh: any, idx: number) => (
                                <tr key={idx}>
                                  <td>{qh.quoteDate ? moment(qh.quoteDate).format('DD.MM.YY') : '-'}</td>
                                  <td className="fw-semibold text-primary">{qh.quoteNumber || '-'}</td>
                                  <td className="text-truncate" style={{ maxWidth: '140px' }} title={qh.customerName}>
                                    {qh.customerName || '-'}
                                  </td>
                                  <td className="text-end fw-bold">{qh.quantity}</td>
                                  <td className="text-end">
                                    {qh.unitPrice > 0 ? (
                                      <span>
                                        {qh.currency === 'USD' ? '$' : qh.currency === 'EUR' ? '€' : '₺'}
                                        {qh.unitPrice.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                                      </span>
                                    ) : (
                                      '-'
                                    )}
                                  </td>
                                  <td className="text-center">
                                    <span
                                      className={`badge badge-light-${
                                        qh.status === 'approved'
                                          ? 'success'
                                          : qh.status === 'sent'
                                          ? 'info'
                                          : qh.status === 'rejected'
                                          ? 'danger'
                                          : qh.status === 'draft'
                                          ? 'secondary'
                                          : qh.status === 'expired'
                                          ? 'warning'
                                          : 'dark'
                                      } fs-9`}
                                    >
                                      {qh.status === 'approved'
                                        ? 'Onaylı'
                                        : qh.status === 'sent'
                                        ? 'Gönderildi'
                                        : qh.status === 'rejected'
                                        ? 'Reddedildi'
                                        : qh.status === 'draft'
                                        ? 'Taslak'
                                        : qh.status === 'expired'
                                        ? 'Süresi Doldu'
                                        : qh.status || '-'}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Reservation Modal */}
      <QuickReservationModal show={showReservationModal} onClose={() => setShowReservationModal(false)} />
      <ApolloContactModal show={showApolloModal} onClose={() => setShowApolloModal(false)} />
    </>
  );
};

const DashboardWrapper: FC = () => {
  const intl = useIntl();
  const onErrorFetch = (e: any) => e?.response?.data?.message && toast.error(e?.response?.data?.message);
  const onErrorFetchEmpty = (e: any) => {};
  const [filters, setFilters] = useState<any>({});
  const [filtersGib, setFiltersGib] = useState<any>({ approvalStatus: '0' });
  const [sign, setSign] = React.useState(false);

  const { refetch, data: dashboardList, isLoading, isFetching } = useDashboardList({ ...filters }, onErrorFetch);

  const triggerPaginationHomeworkPast = (event: any) => {
    setFilters((prevState: any) => ({
      ...prevState,
      pageHomeworkPast: event.selected + 1,
    }));
  };
  useEffect(() => {
    refetch();
  }, [filters.pageHomeworkPast, filters.limitHomeworkPast, filters.sortByHomeworkPast]);
  let history = useHistory();

  const getSessionData = () => {
    //fetching username from sesstion storage
    return sessionStorage.getItem('credentials') as string;
  };

  const [list, setList] = useState<any>();
  const [draftInvoiceApproveModal, setDraftInvoiceApproveModal] = useState<boolean>(false);
  const [draftInvoiceDeleteModal, setDraftInvoiceDeleteModal] = useState<boolean>(false);

  const onSuccessSendSMSFetch = (e: any) => {
    setList((prevState: any) => ({
      ...prevState,
      operationId: e?.data,
    }));
    setDraftInvoiceApproveModal(true);
    toast.success(`İmzalama için sms gönderildi.`);
  };
  const onErrorSendSMSFetch = (e: any) => {
    e?.response?.data?.message && toast.error(e?.response?.data?.message);
  };

  const handleDeleteDraft = (list: any) => {
    const parseCredentials = JSON.parse(getSessionData());
    setList({ ...list, ...parseCredentials });
    setDraftInvoiceDeleteModal(true);
  };

  return (
    <>
      <PageTitle breadcrumbs={[]}>{intl.formatMessage({ id: 'MENU.DASHBOARD' })}</PageTitle>
      <Restricted to="ROLE_DASHBOARD_WIDGET1" fallback={<DashboardWelcome />}>
        {/* <DashboardPage
          dashboardList={dashboardList}
          history={history}
          
          handleSendSMS={handleSendSMS}
          draftInvoiceApproveModal={draftInvoiceApproveModal}
          setDraftInvoiceApproveModal={setDraftInvoiceApproveModal}
          list={list}
          handleDeleteDraft={handleDeleteDraft}
          draftInvoiceDeleteModal={draftInvoiceDeleteModal}
          setDraftInvoiceDeleteModal={setDraftInvoiceDeleteModal}
        /> */}
        <DashboardPage />
        <TermsAndConditions />
        <InviteCompany />
      </Restricted>
    </>
  );
};

export { DashboardWrapper };
